import React, { Component } from 'react';
import { isEmpty } from 'lodash';
import Alert from 'components/ui/Alert';
import PhoneField from 'components/v2/forms/PhoneField';
import { Translation, withTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@cargomatic/ui';
import CargomaticInput from 'components/v2/input';
import UserActionCreators from '../../../actions/UserActionCreators';
import SessionStore from '../../../stores/SessionStore';
import { CARRIER_SUBSIDIARY_OPTIONS } from 'constants/subsidiary';

const getEmptyState = () => {
  return {
    carrier: {
      company_name: '',
      email: '',
      dot_number: '',
      first_name: '',
      last_name: '',
      password: '',
      phone: '',
      phone_extension: '',
      subsidiary: '',
    },
    isCustomerManager: SessionStore.isCustomerManagerAdmin(),
  };
};

class RegisterCarrier extends Component {
  constructor(props) {
    super(props);
    this.state = getEmptyState();
  }

  handlePhoneChange = (phone) => {
    this.setState((prevState) => {
      return {
        carrier: {
          ...prevState.carrier,
          phone,
        },
      };
    });
  };

  handleChange = (field) => (e) => {
    const {
      target: { value },
    } = e;
    this.setState((prevState) => {
      return {
        carrier: {
          ...prevState.carrier,
          [field]: value,
        },
      };
    });
  };

  handleCargomaticInputChange = (field, value) => {
    this.setState((prevState) => {
      return {
        carrier: {
          ...prevState.carrier,
          [field]: value,
        },
      };
    });
  };

  handleRegister = (e) => {
    e.preventDefault();
    const { carrier } = this.state;
    UserActionCreators.addCarrier({
      ...carrier,
    });
    this.setState(getEmptyState());
  };

  render() {
    const { carrier, isCustomerManager } = this.state;
    const { t } = this.props;
    const disabled =
      isEmpty(carrier.company_name) ||
      isEmpty(carrier.dot_number) ||
      isEmpty(carrier.email) ||
      isEmpty(carrier.first_name) ||
      isEmpty(carrier.last_name) ||
      isEmpty(carrier.password) ||
      isEmpty(carrier.phone) ||
      !isCustomerManager;

    return (
      <Card>
        <form onSubmit={this.handleRegister}>
          <CardHeader>
            <CardTitle>{t('ADD_CARRIER')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {!isCustomerManager && (
              <Alert type="warning">
                <Translation>
                  {(t) => <p>{t('REQUIRED_CUSTOMER_MANAGER_PRIVILEGES')}</p>}
                </Translation>
              </Alert>
            )}
            <div className="grid grid-cols-4 gap-6 max-w-3xl">
              <div className="col-span-4 sm:col-span-2">
                <label className="block mb-2 form-label">
                  {t('FIRST_NAME')}
                </label>
                <input
                  className="w-full mb-4 form-field"
                  type="text"
                  placeholder="John"
                  onChange={this.handleChange('first_name')}
                  value={carrier.first_name}
                  disabled={!isCustomerManager}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block mb-2 form-label">
                  {t('LAST_NAME')}
                </label>
                <input
                  className="w-full mb-4 form-field"
                  type="text"
                  placeholder="Smith"
                  onChange={this.handleChange('last_name')}
                  value={carrier.last_name}
                  disabled={!isCustomerManager}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block mb-2 form-label">
                  <span>{t('COMPANY_NAME')}</span>
                </label>
                <input
                  className="w-full mb-4 form-field"
                  type="text"
                  placeholder={t('COMPANY_NAME')}
                  onChange={this.handleChange('company_name')}
                  value={carrier.company_name}
                  disabled={!isCustomerManager}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <CargomaticInput
                  className="block mb-2 form-label"
                  inputClassNames="w-full mb-4 form-field"
                  label={t('DOT_NUMBER')}
                  type="text"
                  placeholder={t('DOT_NUMBER')}
                  onChange={(value) => {
                    this.handleCargomaticInputChange('dot_number', value);
                  }}
                  value={carrier.dot_number}
                  disabled={!isCustomerManager}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block mb-2 form-label">{t('PHONE')}</label>
                <PhoneField
                  value={carrier.phone}
                  onChange={this.handlePhoneChange}
                  disabled={!isCustomerManager}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <CargomaticInput
                  className="block mb-2 form-label"
                  inputClassNames="w-full mb-4 form-field"
                  type="text"
                  label={t('PHONE_EXTENSION')}
                  placeholder={t('PHONE_EXTENSION')}
                  onChange={(value) => {
                    this.handleCargomaticInputChange('phone_extension', value);
                  }}
                  value={carrier.phone_extension}
                  disabled={!isCustomerManager}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block mb-2 form-label">{t('EMAIL')}</label>
                <input
                  className="w-full mb-4 form-field"
                  type="email"
                  placeholder="example@cargomatic.com"
                  onChange={this.handleChange('email')}
                  value={carrier.email}
                  disabled={!isCustomerManager}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block mb-2 form-label">{t('PASSWORD')}</label>
                <input
                  className="w-full mb-4 form-field"
                  type="password"
                  placeholder={t('PASSWORD')}
                  onChange={this.handleChange('password')}
                  value={carrier.password}
                  disabled={!isCustomerManager}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block mb-2 form-label">
                  <span>{t('SUBSIDIARY')}</span>
                </label>
                <select
                  className="mb-4 form-select"
                  onChange={this.handleChange('subsidiary')}
                  value={carrier.subsidiary || ''}
                  disabled={!isCustomerManager}
                >
                  <option value="">{t('SELECT_SUBSIDIARY')}</option>
                  {CARRIER_SUBSIDIARY_OPTIONS.map((subsidiary) => (
                    <option key={subsidiary.key} value={subsidiary.value}>
                      {t(subsidiary.localizationSlug)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
          <CardFooter className="justify-end">
            <Button disabled={disabled} type="submit">
              {t('REGISTER_CARRIER')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }
}

export default withTranslation()(RegisterCarrier);
